import {
  useMutation,
  useQuery,
  useQueryClient,
  UseMutationResult,
  UseQueryResult,
} from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import {
  ErrorNotification,
  type Conversation,
  type Pagination,
} from "@via/utils/interfaces.tsx";
import type { McpToolCallInfo } from "common";

// Backend response type (before transformation)
interface ConversationRecord {
  id: string;
  userId: number;
  contextRoutePattern: string;
  contextName: string;
  pageUrl: string | null;
  title: string | null;
  conversationHistory: Array<{
    role: "user" | "assistant" | "system";
    content: string;
    mcpToolCall?: McpToolCallInfo;
  }>;
  tokenId: number | null;
  model: string | null;
  contextFiles: Array<{
    filePath: string;
    runUUID: string;
    reportName?: string;
    reportExtension?: string;
  }> | null;
  createdAt: string; // ISO date string from backend
  updatedAt: string; // ISO date string from backend
  isPinned: boolean;
}

// Transform backend response to frontend Conversation format
function transformConversation(record: ConversationRecord): Conversation {
  return {
    id: record.id,
    title: record.title,
    conversationHistory: record.conversationHistory,
    createdAt: new Date(record.createdAt).getTime(),
    updatedAt: new Date(record.updatedAt).getTime(),
    contextRoutePattern: record.contextRoutePattern,
    contextName: record.contextName,
    pageUrl: record.pageUrl ?? undefined,
    tokenId: record.tokenId ?? undefined,
    model: record.model ?? undefined,
    contextFiles: record.contextFiles ?? undefined,
    isPinned: record.isPinned ?? false,
  };
}

interface ListConversationsParams {
  page: number;
  pageSize: number;
  searchKey?: string;
  pinnedOnly?: boolean;
}

const fetchConversations = async ({
  page,
  pageSize,
  searchKey,
  pinnedOnly,
}: ListConversationsParams): Promise<Pagination<Conversation>> => {
  try {
    const skip = page * pageSize;
    const take = pageSize;

    const queryParams = new URLSearchParams({
      skip: `${skip}`,
      take: `${take}`,
    });

    if (searchKey && searchKey.trim().length > 0) {
      queryParams.append("searchKey", searchKey.trim());
    }

    if (pinnedOnly) {
      queryParams.append("pinnedOnly", "true");
    }

    const url = `/api/v1/ai-insight/conversations?${queryParams.toString()}`;

    const res = await axios.get(url);

    const { total, skip: resSkip, take: resTake, data } = res.data;

    return {
      total,
      skip: resSkip ?? skip,
      take: resTake ?? take,
      data: data.map(transformConversation),
    };
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const fetchConversation = async (id: string): Promise<Conversation> => {
  try {
    const res = await axios.get<ConversationRecord>(
      `/api/v1/ai-insight/conversations/${id}`
    );
    return transformConversation(res.data);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const createConversation = async (
  payload: Omit<Conversation, "id" | "createdAt" | "updatedAt" | "isPending">
): Promise<Conversation> => {
  try {
    const res = await axios.post<ConversationRecord>(
      "/api/v1/ai-insight/conversations",
      {
        contextRoutePattern: payload.contextRoutePattern,
        contextName: payload.contextName,
        pageUrl: payload.pageUrl ?? null,
        title: payload.title,
        conversationHistory: payload.conversationHistory,
        tokenId: payload.tokenId ?? null,
        model: payload.model ?? null,
        contextFiles: payload.contextFiles ?? null,
      }
    );
    return transformConversation(res.data);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const updateConversation = async (
  id: string,
  payload: Partial<
    Pick<
      Conversation,
      "conversationHistory" | "title" | "contextFiles" | "isPinned" | "pageUrl"
    >
  >
): Promise<Conversation> => {
  try {
    const res = await axios.put<ConversationRecord>(
      `/api/v1/ai-insight/conversations/${id}`,
      {
        conversationHistory: payload.conversationHistory ?? undefined,
        title: payload.title ?? undefined,
        contextFiles: payload.contextFiles ?? undefined,
        isPinned: payload.isPinned ?? undefined,
        pageUrl: payload.pageUrl ?? undefined,
      }
    );
    return transformConversation(res.data);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const deleteConversation = async (
  id: string
): Promise<{ success: boolean }> => {
  try {
    const res = await axios.delete<{ success: boolean }>(
      `/api/v1/ai-insight/conversations/${id}`
    );
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

interface UseListConversationsOptions {
  page: number;
  pageSize: number;
  searchKey?: string;
  enabled?: boolean;
  pinnedOnly?: boolean;
}

export const useListConversations = ({
  page,
  pageSize,
  searchKey,
  enabled = true,
  pinnedOnly = false,
}: UseListConversationsOptions): UseQueryResult<Pagination<Conversation>> => {
  return useQuery<Pagination<Conversation>>({
    queryKey: [
      "/api/v1/ai-insight/conversations",
      page,
      pageSize,
      searchKey ?? "",
      pinnedOnly,
    ],
    queryFn: () =>
      fetchConversations({ page, pageSize, searchKey, pinnedOnly }),
    retry: false,
    enabled,
  });
};

export const useGetConversation = (
  id: string | null
): UseQueryResult<Conversation> => {
  return useQuery({
    queryKey: ["/api/v1/ai-insight/conversations", id],
    queryFn: () => fetchConversation(id!),
    enabled: !!id,
    retry: false,
  });
};

export const useCreateConversation = (): UseMutationResult<
  Conversation,
  ErrorNotification,
  Omit<Conversation, "id" | "createdAt" | "updatedAt" | "isPending">
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: createConversation,
    onSuccess: (data): void => {
      setNotification({
        id: "conversation-created",
        message: "Conversation Created",
        variant: NOTIFICATION.SUCCESS,
      });
      // Invalidate conversations list
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/ai-insight/conversations"],
      });
      // Set the new conversation in cache
      queryClient.setQueryData(
        ["/api/v1/ai-insight/conversations", data.id],
        data
      );
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "conversation-creation-failed",
        message: "Conversation Creation Failed",
        details:
          error.response?.data?.message ?? "Conversation could not be created",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useUpdateConversation = (): UseMutationResult<
  Conversation,
  ErrorNotification,
  {
    id: string;
    payload: Partial<
      Pick<
        Conversation,
        | "conversationHistory"
        | "title"
        | "contextFiles"
        | "isPinned"
        | "pageUrl"
      >
    >;
  }
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: ({ id, payload }) => updateConversation(id, payload),
    onSuccess: (data, variables): void => {
      setNotification({
        id: "conversation-updated",
        message: "Conversation Updated",
        variant: NOTIFICATION.SUCCESS,
      });
      // Invalidate conversations list and update cache
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/ai-insight/conversations"],
      });
      queryClient.setQueryData(
        ["/api/v1/ai-insight/conversations", variables.id],
        data
      );
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "conversation-update-failed",
        message: "Conversation Update Failed",
        details:
          error.response?.data?.message ?? "Conversation could not be updated",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useDeleteConversation = (): UseMutationResult<
  { success: boolean },
  ErrorNotification,
  string
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: deleteConversation,
    onSuccess: (_, conversationId): void => {
      setNotification({
        id: "conversation-deleted",
        message: "Conversation Deleted",
        variant: NOTIFICATION.SUCCESS,
      });
      // Remove from cache and invalidate list
      queryClient.removeQueries({
        queryKey: ["/api/v1/ai-insight/conversations", conversationId],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/ai-insight/conversations"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "conversation-deletion-failed",
        message: "Conversation Deletion Failed",
        details:
          error.response?.data?.message ?? "Conversation could not be deleted",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};
